import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import {
  Settings,
  Building2,
  CreditCard,
  Mail,
  Globe,
  Save,
  Loader2,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Link,
  Unlink,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';

type TabId = 'organization' | 'payments' | 'email' | 'integrations';

interface Organization {
  id: string;
  name: string;
  slug: string;
  timezone: string;
  logoUrl: string | null;
  website: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
}

const tabs: { id: TabId; name: string; icon: typeof Settings }[] = [
  { id: 'organization', name: 'Organization', icon: Building2 },
  { id: 'payments', name: 'Payments', icon: CreditCard },
  { id: 'email', name: 'Email', icon: Mail },
  { id: 'integrations', name: 'Integrations', icon: Link },
];

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('organization');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Configure your organization settings
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 border-b-2 py-3 px-1 text-sm font-medium transition-colors',
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'organization' && <OrganizationTab />}
      {activeTab === 'payments' && <PaymentsTab />}
      {activeTab === 'email' && <EmailTab />}
      {activeTab === 'integrations' && <IntegrationsTab />}
    </div>
  );
}

function OrganizationTab() {
  const queryClient = useQueryClient();
  const [showSuccess, setShowSuccess] = useState(false);

  const { data: org, isLoading } = useQuery({
    queryKey: ['organization'],
    queryFn: async () => {
      const response = await api.get<Organization>('/organizations/current');
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  const [formData, setFormData] = useState<Partial<Organization>>({});

  // Initialize form when data loads
  const currentData = { ...org, ...formData };

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<Organization>) => {
      const response = await api.patch('/organizations/current', data);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      setFormData({});
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {showSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <p className="text-green-800">Settings saved successfully!</p>
        </div>
      )}

      {/* Basic Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organization Name
            </label>
            <input
              type="text"
              value={currentData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL Slug
            </label>
            <div className="flex items-center">
              <span className="text-sm text-gray-500 mr-1">openshul.com/</span>
              <input
                type="text"
                value={currentData.slug || ''}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Website
            </label>
            <input
              type="url"
              value={currentData.website || ''}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              placeholder="https://"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timezone
            </label>
            <select
              value={currentData.timezone || 'America/New_York'}
              onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="America/New_York">Eastern Time</option>
              <option value="America/Chicago">Central Time</option>
              <option value="America/Denver">Mountain Time</option>
              <option value="America/Los_Angeles">Pacific Time</option>
              <option value="America/Phoenix">Arizona Time</option>
              <option value="Pacific/Honolulu">Hawaii Time</option>
            </select>
          </div>
        </div>
      </div>

      {/* Contact Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={currentData.email || ''}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              value={currentData.phone || ''}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <input
              type="text"
              value={currentData.address || ''}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              City
            </label>
            <input
              type="text"
              value={currentData.city || ''}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <input
                type="text"
                value={currentData.state || ''}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Postal Code
              </label>
              <input
                type="text"
                value={currentData.postalCode || ''}
                onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={updateMutation.isPending || Object.keys(formData).length === 0}
          className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {updateMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save Changes
            </>
          )}
        </button>
      </div>
    </form>
  );
}

interface OrgSettings {
  stripePublishableKey?: string;
  smtpHost?: string;
  smtpPort?: string;
  smtpFromEmail?: string;
  smtpFromName?: string;
  smtpSecure?: boolean;
}

function PaymentsTab() {
  const queryClient = useQueryClient();
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState<{ stripePublishableKey: string }>({
    stripePublishableKey: '',
  });

  const { data: org, isLoading } = useQuery({
    queryKey: ['organization'],
    queryFn: async () => {
      const response = await api.get<Organization & { settings: OrgSettings }>('/organizations/current');
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  const { data: config } = useQuery({
    queryKey: ['payment-config'],
    queryFn: async () => {
      const response = await api.get<{ stripeConfigured: boolean }>('/payments/config');
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (settings: OrgSettings) => {
      const currentSettings = (org?.settings || {}) as OrgSettings;
      const response = await api.patch('/organizations/current', {
        settings: { ...currentSettings, ...settings },
      });
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      queryClient.invalidateQueries({ queryKey: ['payment-config'] });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    },
  });

  const currentSettings = (org?.settings || {}) as OrgSettings;
  const currentData = {
    stripePublishableKey: formData.stripePublishableKey || currentSettings.stripePublishableKey || '',
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.stripePublishableKey) {
      updateMutation.mutate({ stripePublishableKey: formData.stripePublishableKey });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {showSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <p className="text-green-800">Payment settings saved successfully!</p>
        </div>
      )}

      {/* Stripe Configuration */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Stripe Integration</h2>
            <p className="text-sm text-gray-500">Accept online payments with Stripe</p>
          </div>
          {config?.stripeConfigured || currentData.stripePublishableKey ? (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
              <CheckCircle className="h-3 w-3" />
              Connected
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              Not configured
            </span>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stripe Publishable Key
            </label>
            <input
              type="text"
              value={currentData.stripePublishableKey}
              onChange={(e) => setFormData({ ...formData, stripePublishableKey: e.target.value })}
              placeholder="pk_live_..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Your public Stripe key (starts with pk_)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stripe Secret Key
            </label>
            <input
              type="password"
              placeholder="sk_live_... (set via environment variable)"
              disabled
              className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-gray-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              For security, set via STRIPE_SECRET_KEY environment variable on your server
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Webhook Endpoint
            </label>
            <input
              type="text"
              value={`${window.location.origin}/api/payments/webhook`}
              disabled
              className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-gray-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Configure this URL in your Stripe dashboard. Set STRIPE_WEBHOOK_SECRET on your server.
            </p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={updateMutation.isPending || !formData.stripePublishableKey}
          className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {updateMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save Changes
            </>
          )}
        </button>
      </div>

      {/* Payment Settings Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-sm font-medium text-blue-900 mb-2">Setting Up Payments</h3>
        <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
          <li>Create a Stripe account at stripe.com</li>
          <li>Get your API keys from the Stripe Dashboard</li>
          <li>Enter your Publishable Key above</li>
          <li>Add STRIPE_SECRET_KEY to your server environment</li>
          <li>Set up webhooks pointing to the webhook URL above</li>
          <li>Add STRIPE_WEBHOOK_SECRET to your server environment</li>
        </ol>
      </div>
    </form>
  );
}

function EmailTab() {
  const queryClient = useQueryClient();
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState<Partial<OrgSettings>>({});

  const { data: org, isLoading } = useQuery({
    queryKey: ['organization'],
    queryFn: async () => {
      const response = await api.get<Organization & { settings: OrgSettings }>('/organizations/current');
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (settings: Partial<OrgSettings>) => {
      const currentSettings = (org?.settings || {}) as OrgSettings;
      const response = await api.patch('/organizations/current', {
        settings: { ...currentSettings, ...settings },
      });
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      setFormData({});
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    },
  });

  const testMutation = useMutation({
    mutationFn: async (email: string) => {
      const response = await api.post('/notifications/test-email', { email });
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      setTestResult({ success: true, message: 'Test email sent successfully!' });
    },
    onError: (error) => {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to send test email',
      });
    },
  });

  const currentSettings = (org?.settings || {}) as OrgSettings;
  const currentData = {
    smtpHost: formData.smtpHost ?? currentSettings.smtpHost ?? '',
    smtpPort: formData.smtpPort ?? currentSettings.smtpPort ?? '587',
    smtpFromEmail: formData.smtpFromEmail ?? currentSettings.smtpFromEmail ?? '',
    smtpFromName: formData.smtpFromName ?? currentSettings.smtpFromName ?? org?.name ?? '',
    smtpSecure: formData.smtpSecure ?? currentSettings.smtpSecure ?? false,
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (Object.keys(formData).length > 0) {
      updateMutation.mutate(formData);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <p className="text-green-800">Email settings saved successfully!</p>
        </div>
      )}

      {/* Email Configuration */}
      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Email Configuration</h2>

          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Host
                </label>
                <input
                  type="text"
                  value={currentData.smtpHost}
                  onChange={(e) => setFormData({ ...formData, smtpHost: e.target.value })}
                  placeholder="smtp.sendgrid.net"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Port
                </label>
                <input
                  type="text"
                  value={currentData.smtpPort}
                  onChange={(e) => setFormData({ ...formData, smtpPort: e.target.value })}
                  placeholder="587"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Email
                </label>
                <input
                  type="email"
                  value={currentData.smtpFromEmail}
                  onChange={(e) => setFormData({ ...formData, smtpFromEmail: e.target.value })}
                  placeholder="noreply@yoursynagogue.org"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Name
                </label>
                <input
                  type="text"
                  value={currentData.smtpFromName}
                  onChange={(e) => setFormData({ ...formData, smtpFromName: e.target.value })}
                  placeholder="My Synagogue"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={currentData.smtpSecure}
                onChange={(e) => setFormData({ ...formData, smtpSecure: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Use SSL/TLS (port 465)</span>
            </label>

            <div className="border-t border-gray-200 pt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Username / Password
                </label>
                <input
                  type="password"
                  placeholder="Set via SMTP_USER and SMTP_PASS environment variables"
                  disabled
                  className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-gray-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  For security, authentication credentials are configured via server environment variables
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end mb-6">
          <button
            type="submit"
            disabled={updateMutation.isPending || Object.keys(formData).length === 0}
            className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {updateMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>

      {/* Test Email */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Test Email Configuration</h2>

        <div className="flex gap-4">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="Enter email address"
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <button
            onClick={() => testMutation.mutate(testEmail)}
            disabled={testMutation.isPending || !testEmail}
            className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {testMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Mail className="h-4 w-4" />
            )}
            Send Test
          </button>
        </div>

        {testResult && (
          <div
            className={cn(
              'mt-4 p-3 rounded-lg flex items-center gap-2',
              testResult.success
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-700'
            )}
          >
            {testResult.success ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            {testResult.message}
          </div>
        )}
      </div>

      {/* Supported Providers */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-sm font-medium text-blue-900 mb-2">Supported Email Providers</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>SendGrid</strong> - smtp.sendgrid.net (use API key as password)</li>
          <li>• <strong>Mailgun</strong> - smtp.mailgun.org</li>
          <li>• <strong>Amazon SES</strong> - email-smtp.[region].amazonaws.com</li>
          <li>• <strong>Gmail</strong> - smtp.gmail.com (requires app password)</li>
          <li>• <strong>Custom SMTP</strong> - Any standard SMTP server</li>
        </ul>
      </div>
    </div>
  );
}

interface IntegrationStatus {
  configured: boolean;
  connected: boolean;
  realmId?: string;
  tenantId?: string;
  companyName?: string;
  expiresAt?: string;
  message?: string;
}

function IntegrationsTab() {
  const queryClient = useQueryClient();

  const { data: qboStatus, isLoading: qboLoading } = useQuery({
    queryKey: ['integrations', 'quickbooks'],
    queryFn: async () => {
      const response = await api.get<IntegrationStatus>('/integrations/quickbooks/status');
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  const { data: xeroStatus, isLoading: xeroLoading } = useQuery({
    queryKey: ['integrations', 'xero'],
    queryFn: async () => {
      const response = await api.get<IntegrationStatus>('/integrations/xero/status');
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  const connectQboMutation = useMutation({
    mutationFn: async () => {
      const response = await api.get<{ authUrl: string }>('/integrations/quickbooks/authorize');
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: (data) => {
      // Redirect to QuickBooks OAuth page
      if (data?.authUrl) {
        window.location.href = data.authUrl;
      }
    },
  });

  const disconnectQboMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/integrations/quickbooks/disconnect', {});
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'quickbooks'] });
    },
  });

  const syncMembersMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<{
        total: number;
        alreadySynced: number;
        newlySynced: number;
        failed: number;
      }>('/integrations/quickbooks/sync/members', {});
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  // Xero mutations
  const connectXeroMutation = useMutation({
    mutationFn: async () => {
      const response = await api.get<{ authUrl: string }>('/integrations/xero/authorize');
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: (data) => {
      if (data?.authUrl) {
        window.location.href = data.authUrl;
      }
    },
  });

  const disconnectXeroMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/integrations/xero/disconnect', {});
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'xero'] });
    },
  });

  const syncXeroMembersMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<{
        total: number;
        alreadySynced: number;
        newlySynced: number;
        failed: number;
      }>('/integrations/xero/sync/members', {});
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  const isLoading = qboLoading || xeroLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* QuickBooks Online */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Globe className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">QuickBooks Online</h2>
              <p className="text-sm text-gray-500">Sync members, invoices, and payments</p>
            </div>
          </div>
          {qboStatus?.connected ? (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
              <CheckCircle className="h-3 w-3" />
              Connected
            </span>
          ) : qboStatus?.configured ? (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
              <AlertCircle className="h-3 w-3" />
              Not connected
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              Not configured
            </span>
          )}
        </div>

        {!qboStatus?.configured ? (
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-2">
              QuickBooks integration is not configured. To enable:
            </p>
            <ol className="text-sm text-gray-600 list-decimal list-inside space-y-1">
              <li>Create an app in the Intuit Developer Portal</li>
              <li>Set the redirect URI to: <code className="bg-gray-200 px-1 rounded">{window.location.origin}/api/integrations/quickbooks/callback</code></li>
              <li>Add QBO_CLIENT_ID, QBO_CLIENT_SECRET, and QBO_REDIRECT_URI to your server environment</li>
            </ol>
          </div>
        ) : qboStatus.connected ? (
          <div className="space-y-4">
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-900">Connected to QuickBooks</p>
                  {qboStatus.realmId && (
                    <p className="text-xs text-green-700 mt-1">
                      Company ID: {qboStatus.realmId}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => disconnectQboMutation.mutate()}
                  disabled={disconnectQboMutation.isPending}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                >
                  {disconnectQboMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Unlink className="h-4 w-4" />
                  )}
                  Disconnect
                </button>
              </div>
            </div>

            {/* Sync Actions */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Sync Data</h3>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => syncMembersMutation.mutate()}
                  disabled={syncMembersMutation.isPending}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {syncMembersMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Sync All Members
                </button>
              </div>

              {syncMembersMutation.isSuccess && syncMembersMutation.data && (
                <div className="mt-3 bg-green-50 text-green-700 text-sm p-3 rounded-lg">
                  Sync complete: {syncMembersMutation.data.newlySynced} members synced,{' '}
                  {syncMembersMutation.data.alreadySynced} already synced,{' '}
                  {syncMembersMutation.data.failed} failed
                </div>
              )}

              {syncMembersMutation.isError && (
                <div className="mt-3 bg-red-50 text-red-700 text-sm p-3 rounded-lg">
                  Sync failed: {syncMembersMutation.error instanceof Error ? syncMembersMutation.error.message : 'Unknown error'}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex justify-end">
            <button
              onClick={() => connectQboMutation.mutate()}
              disabled={connectQboMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
            >
              {connectQboMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ExternalLink className="h-4 w-4" />
              )}
              Connect to QuickBooks
            </button>
          </div>
        )}
      </div>

      {/* Xero */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Globe className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Xero</h2>
              <p className="text-sm text-gray-500">Sync members, invoices, and payments</p>
            </div>
          </div>
          {xeroStatus?.connected ? (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
              <CheckCircle className="h-3 w-3" />
              Connected
            </span>
          ) : xeroStatus?.configured ? (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
              <AlertCircle className="h-3 w-3" />
              Not connected
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              Not configured
            </span>
          )}
        </div>

        {!xeroStatus?.configured ? (
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-2">
              Xero integration is not configured. To enable:
            </p>
            <ol className="text-sm text-gray-600 list-decimal list-inside space-y-1">
              <li>Create an app in the Xero Developer Portal</li>
              <li>Set the redirect URI to: <code className="bg-gray-200 px-1 rounded">{window.location.origin}/api/integrations/xero/callback</code></li>
              <li>Add XERO_CLIENT_ID, XERO_CLIENT_SECRET, and XERO_REDIRECT_URI to your server environment</li>
            </ol>
          </div>
        ) : xeroStatus.connected ? (
          <div className="space-y-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-900">Connected to Xero</p>
                  {xeroStatus.tenantId && (
                    <p className="text-xs text-blue-700 mt-1">
                      Tenant ID: {xeroStatus.tenantId}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => disconnectXeroMutation.mutate()}
                  disabled={disconnectXeroMutation.isPending}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                >
                  {disconnectXeroMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Unlink className="h-4 w-4" />
                  )}
                  Disconnect
                </button>
              </div>
            </div>

            {/* Sync Actions */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Sync Data</h3>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => syncXeroMembersMutation.mutate()}
                  disabled={syncXeroMembersMutation.isPending}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {syncXeroMembersMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Sync All Members
                </button>
              </div>

              {syncXeroMembersMutation.isSuccess && syncXeroMembersMutation.data && (
                <div className="mt-3 bg-green-50 text-green-700 text-sm p-3 rounded-lg">
                  Sync complete: {syncXeroMembersMutation.data.newlySynced} members synced,{' '}
                  {syncXeroMembersMutation.data.alreadySynced} already synced,{' '}
                  {syncXeroMembersMutation.data.failed} failed
                </div>
              )}

              {syncXeroMembersMutation.isError && (
                <div className="mt-3 bg-red-50 text-red-700 text-sm p-3 rounded-lg">
                  Sync failed: {syncXeroMembersMutation.error instanceof Error ? syncXeroMembersMutation.error.message : 'Unknown error'}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex justify-end">
            <button
              onClick={() => connectXeroMutation.mutate()}
              disabled={connectXeroMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              {connectXeroMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ExternalLink className="h-4 w-4" />
              )}
              Connect to Xero
            </button>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-sm font-medium text-blue-900 mb-2">About Accounting Integrations</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Members → Customers</strong>: Sync member profiles to your accounting software</li>
          <li>• <strong>Invoices</strong>: Push membership dues and other invoices</li>
          <li>• <strong>Payments</strong>: Record payments when processed through OpenShul</li>
          <li>• <strong>Donations</strong>: Optionally sync donations as sales receipts</li>
        </ul>
      </div>
    </div>
  );
}
